import { lazy, Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "~/auth/AuthProvider";
import PrivateRoute from "~/auth/PrivateRoute";
import { PreferencesProvider } from "~/features/preferences/PreferencesProvider";
import { SnackbarProvider } from "~/shared/ui/feedback/SnackbarProvider";
import { queryClient } from "~/shared/api/queryClient";
import ChunkErrorBoundary from "~/shared/ui/ChunkErrorBoundary";
import { useFeatures } from "~/hooks/useFeatures";
import AntecipeForm from "~/pages/AntecipeForm";
import Login from "~/pages/Login";
import AccessDenied from "~/pages/AccessDenied";
import AdminHome from "~/pages/AdminHome";
import CompanyRequests from "~/pages/privates/medicalcompany/Requests";
import BackOfficeRequests from "~/pages/privates/antecipebackoffice/Requests";
import BackOfficePackages from "~/pages/privates/antecipebackoffice/Packages";
import BackOfficeRegisterDoctor from "~/pages/privates/antecipebackoffice/RegisterDoctor";
import AccessPage from "~/pages/privates/admin/Access";
import DomainsPage from "~/pages/privates/admin/Domains";
import CompaniesPage from "~/pages/privates/admin/Companies";

// Catálogo de motivos (FE-1b): carregado sob demanda, só quando o Admin abre a tela.
const ReasonsCatalogPage = lazy(() => import("~/features/reasons-admin").then((m) => ({ default: m.ReasonsCatalogPage })));
function RouteSkeleton() {
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="route-loading">
      <Skeleton variant="text" width={260} height={36} />
      <Skeleton variant="rounded" height={56} sx={{ my: 2 }} />
      <Skeleton variant="rounded" height={320} />
    </Box>
  );
}
// Dashboard (FE-2a): idem, só quando a pessoa abre a tela; as abas também chegam sob demanda (tabs/registry).
const DashboardPage = lazy(() => import("~/features/dashboard").then((m) => ({ default: m.DashboardPage })));
function DashboardRoute() {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <DashboardPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}
function CatalogRoute() {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <ReasonsCatalogPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Integrações (FE-4a): lazy e atrás da flag integrationsAdmin; com a flag desligada a URL digitada volta para /admin.
const IntegrationsPage = lazy(() => import("~/features/integrations").then((m) => ({ default: m.IntegrationsPage })));
function IntegrationsRoute() {
  const { loading, features } = useFeatures();
  if (loading) return <RouteSkeleton />;
  if (!features.integrationsAdmin) return <Navigate to="/admin" replace />;
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <IntegrationsPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Melhoria contínua (FE-3a0): a página some sozinha com a flag continuousImprovement desligada.
const ContinuousImprovementPage = lazy(() =>
  import("~/features/continuous-improvement").then((m) => ({ default: m.ContinuousImprovementPage }))
);
function ImprovementRoute() {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <ContinuousImprovementPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Feriados (SEASON): lazy, só Admin e atrás da flag seasonality; com a flag desligada a URL digitada volta para /admin.
const HolidaysPage = lazy(() => import("~/features/holidays-admin").then((m) => ({ default: m.HolidaysPage })));
function HolidaysRoute() {
  const { loading, features } = useFeatures();
  if (loading) return <RouteSkeleton />;
  if (!features.seasonality) return <Navigate to="/admin" replace />;
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <HolidaysPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Campanhas (FE-4b): lazy, exige o grant `campaigns` e a flag; com a flag desligada a URL digitada volta para /admin.
const CampaignsPage = lazy(() => import("~/features/campaigns").then((m) => ({ default: m.CampaignsPage })));
function CampaignsRoute() {
  const { loading, features } = useFeatures();
  if (loading) return <RouteSkeleton />;
  if (!features.campaigns) return <Navigate to="/admin" replace />;
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <CampaignsPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

// Premissas financeiras: lazy e atrás da flag financialPremises; com a flag desligada a URL digitada volta para /admin.
const FinancialPremisesPage = lazy(() => import("~/features/financial-premises").then((m) => ({ default: m.FinancialPremisesPage })));
function FinancialPremisesRoute() {
  const { loading, features } = useFeatures();
  if (loading) return <RouteSkeleton />;
  if (!features.financialPremises) return <Navigate to="/admin" replace />;
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <FinancialPremisesPage />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

const priv = (path, require, page) => ({ path, element: <PrivateRoute require={require} page={page} /> });

// AuthProvider só na árvore /admin: o formulário público do médico nunca chama auth/me.
// QueryClientProvider (TanStack Query v5) também só no /admin: dado remoto novo do dashboard.
// SnackbarProvider fica acima do PreferencesProvider para ele poder avisar (o AppShell não monta outro).
export const routes = [
  { path: "/", element: <AntecipeForm /> },
  { path: "/login", element: <Login /> },
  { path: "/convite/:token", element: <Login /> },
  { path: "/acesso-negado", element: <AccessDenied /> },
  {
    path: "/admin",
    element: (
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider>
            <PreferencesProvider>
              <Outlet />
            </PreferencesProvider>
          </SnackbarProvider>
        </QueryClientProvider>
      </AuthProvider>
    ),
    children: [
      { index: true, element: <AdminHome /> },
      priv("empresa/solicitacoes", "companyScreens", CompanyRequests),
      priv("antecipe/dashboard/:tab?", "backofficeRead", DashboardRoute),
      priv("antecipe/solicitacoes", "backofficeRead", BackOfficeRequests),
      priv("antecipe/pacotes", "backofficeRead", BackOfficePackages),
      priv("antecipe/cadastrar-medico", "backofficeRead", BackOfficeRegisterDoctor),
      priv("acessos", "admin", AccessPage),
      priv("dominios", "admin", DomainsPage),
      priv("empresas", "admin", CompaniesPage),
      priv("catalogo-motivos", "admin", CatalogRoute),
      priv("integracoes", "admin", IntegrationsRoute),
      priv("antecipe/melhoria-continua", "backofficeRead", ImprovementRoute),
      priv("antecipe/campanhas", "campaigns", CampaignsRoute),
      priv("antecipe/premissas-financeiras", "backofficeRead", FinancialPremisesRoute),
      priv("antecipe/feriados", "admin", HolidaysRoute),
      { path: "*", element: <AdminHome /> },
    ],
  },
];
